import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CalendarDays, Headphones, MessageCircle, Phone, ShieldCheck } from "lucide-react";

const supportIcons = [
  { icon: Headphones, angle: 0, color: "text-blue-600", background: "from-blue-50 to-white", shadow: "shadow-blue-900/10" },
  { icon: MessageCircle, angle: 72, color: "text-indigo-600", background: "from-indigo-50 to-white", shadow: "shadow-indigo-900/10" },
  { icon: ShieldCheck, angle: 144, color: "text-emerald-600", background: "from-emerald-50 to-white", shadow: "shadow-emerald-900/10" },
  { icon: CalendarDays, angle: 216, color: "text-blue-600", background: "from-sky-50 to-white", shadow: "shadow-sky-900/10" },
  { icon: Phone, angle: 288, color: "text-violet-600", background: "from-violet-50 to-white", shadow: "shadow-violet-900/10" },
];

const orbitTransition = { duration: 36, repeat: Infinity, ease: "linear" };

export default function HomeSupportVisual() {
  const reduceMotion = useReducedMotion();

  return (
    <div className="relative isolate aspect-square w-full max-w-[440px]">
      <div className="pointer-events-none absolute inset-[12%] -z-10 rounded-full bg-[radial-gradient(circle,rgba(219,234,254,0.7)_0%,rgba(238,242,255,0.4)_45%,transparent_72%)] blur-2xl" aria-hidden="true" />

      <div className="pointer-events-none absolute inset-[5%] rounded-full border border-blue-100/90 shadow-[inset_0_0_24px_rgba(219,234,254,0.16)]" aria-hidden="true" />
      <div className="pointer-events-none absolute inset-[16%] rounded-full border border-dashed border-indigo-100/80" aria-hidden="true" />
      <div className="pointer-events-none absolute bottom-[20%] left-[20%] h-8 w-3/5 rounded-full bg-blue-950/15 blur-xl" aria-hidden="true" />

      <div className="absolute inset-0 flex items-center justify-center">
        <motion.div
          className="relative w-[78%]"
          animate={{ y: reduceMotion ? 0 : [0, -7, 0] }}
          transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
        >
          <div className="relative [transform:perspective(1000px)_rotateX(6deg)_rotateY(-12deg)_skewX(6deg)]">
            <div className="absolute inset-0 translate-x-1.5 translate-y-2.5 rounded-[24px] border border-slate-300/60 bg-gradient-to-br from-slate-100 to-slate-300/80" aria-hidden="true" />
            <div className="relative overflow-hidden rounded-[24px] border border-white bg-gradient-to-b from-white to-slate-100 p-2.5 shadow-[inset_0_1px_0_#fff,0_22px_48px_-18px_rgba(30,64,175,0.28),0_4px_8px_rgba(15,23,42,0.06)] ring-1 ring-slate-900/5">
              <div className="flex h-5 items-start gap-1 px-1" aria-hidden="true">
                <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
                <span className="h-1.5 w-1.5 rounded-full bg-yellow-500" />
                <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                <span className="ml-auto mr-auto h-1.5 w-16 rounded-full bg-slate-200/70" />
              </div>
              <div className="overflow-hidden rounded-2xl border border-slate-200/60 bg-white shadow-[inset_0_1px_3px_rgba(15,23,42,0.04)]">
                <img src="/banner.png" alt="Support Banner" className="block h-auto w-full object-contain" />
              </div>
              <div className="pointer-events-none absolute inset-0 rounded-[24px] bg-gradient-to-br from-white/25 via-transparent to-transparent" aria-hidden="true" />
            </div>
          </div>
        </motion.div>
      </div>

      {supportIcons.map(({ icon: Icon, angle, color, background, shadow }) => (
        <motion.div
          key={angle}
          className="pointer-events-none absolute inset-[5%] z-10"
          initial={{ rotate: angle }}
          animate={{ rotate: reduceMotion ? angle : angle + 360 }}
          transition={orbitTransition}
          aria-hidden="true"
        >
          <div className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2">
            <motion.div
              initial={{ rotate: -angle }}
              animate={{ rotate: reduceMotion ? -angle : -angle - 360 }}
              transition={orbitTransition}
            >
              <span className={`flex h-12 w-12 items-center justify-center rounded-2xl border border-white bg-gradient-to-br shadow-lg ring-1 ring-slate-900/5 xl:h-14 xl:w-14 ${color} ${background} ${shadow}`}>
                <Icon size={23} strokeWidth={1.6} />
              </span>
            </motion.div>
          </div>
        </motion.div>
      ))}
    </div>
  );
}
